import { z } from 'zod';

export const SupplierType = {
   OWN_COMPANY: 'OWN_COMPANY',
   SUPPLIER: 'SUPPLIER',
   BUSINESS_PARTNER: 'BUSINESS_PARTNER',
} as const;
export type SupplierTypeValueEnum = (typeof SupplierType)[keyof typeof SupplierType];

export function getSupplierTypeQueryParam(ownEntities: boolean): string {
   if (ownEntities) return SupplierType.OWN_COMPANY;
   return Object.values(SupplierType)
      .filter((type) => type !== SupplierType.OWN_COMPANY)
      .join(',');
}

export const LowMediumHighEnum = z.enum(['low', 'medium', 'high']);

type _SupplierValueChainPositionEnum =
   | 'MATERIAL_INPUT'
   | 'PRODUCTION'
   | 'TRADER_RAW_PRODUCTION_MATERIAL'
   | 'SEMI_FINISHED_PRODUCT'
   | 'FINISHED_PRODUCT'
   | 'TRADER_FINISHED_PART';

type _SupplierBusinessRelationEnum = 'NON_CRITICAL' | 'BOTTLENECK' | 'STRATEGIC' | 'LEVERAGE';

export const RiskLevels = {
   LOW: 'low',
   MEDIUM: 'medium',
   HIGH: 'high',
} as const;
export type RiskLevelsEnum = (typeof RiskLevels)[keyof typeof RiskLevels];

type _SupplyChainCaseStatusEnum = 'new' | 'open' | 'in_progress' | 'closed';

export const HumanRightsRisks = {
   CHILD_LABOR: 'CHILD_LABOR',
   FORCED_LABOR: 'FORCED_LABOR',
   WORKING_CONDITIONS: 'WORKING_CONDITIONS',
   COLLECTIVE_BARGAINING: 'COLLECTIVE_BARGAINING',
   DISCRIMINATION: 'DISCRIMINATION',
   LAND_RIGHTS: 'LAND_RIGHTS',
   FUNDAMENTAL_RIGHTS: 'FUNDAMENTAL_RIGHTS',
} as const;
export type HumanRightsRisksEnum = (typeof HumanRightsRisks)[keyof typeof HumanRightsRisks];

type _HumanRightsSituationEvidencesEnum = 'ENVIRONMENTAL_MANAGEMENT_SYSTEM' | 'OCCUPATION_HEALTH_SAFETY_SYSTEM' | 'HUMAN_RIGHTS_MANAGEMENT_SYSTEM';

export const EnvironmentalRisks = {
   POLLUTION: 'POLLUTION',
   DEFORESTATION_BIODIVERSITY: 'DEFORESTATION_BIODIVERSITY',
   WASTE: 'WASTE',
   WATER_CONSUMPTION: 'WATER_CONSUMPTION',
} as const;
export type EnvironmentalRisksEnum = (typeof EnvironmentalRisks)[keyof typeof EnvironmentalRisks];

export const GovernanceRisks = {
   CORRUPTION: 'CORRUPTION',
   POLITICAL_STABILITY: 'POLITICAL_STABILITY',
} as const;
export type GovernanceRisksEnum = (typeof GovernanceRisks)[keyof typeof GovernanceRisks];

export const Risks = {
   ...HumanRightsRisks,
   ...EnvironmentalRisks,
   ...GovernanceRisks,
} as const;
export type RisksEnum = (typeof Risks)[keyof typeof Risks];
export const supplyChainRiskCategoryValues = Object.values(Risks);

type _RiskCategoriesEnum = 'HUMAN_RIGHTS' | 'ENVIRONMENTAL' | 'GOVERNANCE';

export const ComplaintSource = {
   COMPLAINT_CHANNEL: 'COMPLAINT_CHANNEL',
   EMAIL: 'EMAIL',
   PHONE: 'PHONE',
   RUMOR: 'RUMOR',
   VERBAL: 'VERBAL',
   OTHER: 'OTHER',
} as const;
export type ComplaintSourceEnum = (typeof ComplaintSource)[keyof typeof ComplaintSource];

export const ComplaintStatus = {
   NEW: 'NEW',
   OPEN: 'OPEN',
   IN_PROGRESS: 'IN_PROGRESS',
   CLOSED: 'CLOSED',
} as const;
export type ComplaintStatusEnum = (typeof ComplaintStatus)[keyof typeof ComplaintStatus];

export const ComplaintPriority = {
   LOW: 'LOW',
   MEDIUM: 'MEDIUM',
   HIGH: 'HIGH',
} as const;
export type ComplaintPriorityEnum = (typeof ComplaintPriority)[keyof typeof ComplaintPriority];

export const CaseStatus = {
   NEW: 'NEW',
   OPEN: 'OPEN',
   IN_PROGRESS: 'IN_PROGRESS',
   CLOSED: 'CLOSED',
} as const;
export type CaseStatusEnum = (typeof CaseStatus)[keyof typeof CaseStatus];

export const CasePriority = {
   UNSPECIFIED: 'UNSPECIFIED',
   LOW: 'LOW',
   MEDIUM: 'MEDIUM',
   HIGH: 'HIGH',
} as const;
export type CasePriorityEnum = (typeof CasePriority)[keyof typeof CasePriority];

export const ActionStatus = {
   OPEN: 'OPEN',
   IN_PROGRESS: 'IN_PROGRESS',
   DONE: 'DONE',
   FAILED: 'FAILED',
   CLOSED: 'CLOSED',
} as const;
export type ActionStatusEnum = (typeof ActionStatus)[keyof typeof ActionStatus];

export const ActionPriority = {
   LOW: 'LOW',
   MEDIUM: 'MEDIUM',
   HIGH: 'HIGH',
} as const;
export type ActionPriorityEnum = (typeof ActionPriority)[keyof typeof ActionPriority];

export const SupplyChainPriority = {
   LOW: 'LOW',
   MEDIUM: 'MEDIUM',
   HIGH: 'HIGH',
} as const;
export type SupplyChainPriorityEnum = ComplaintPriorityEnum | CasePriorityEnum | ActionPriorityEnum;

export const SupplierPriority = {
   LOW: 'LOW',
   MEDIUM: 'MEDIUM',
   HIGH: 'HIGH',
} as const;
export type SupplierPriorityEnum = (typeof SupplierPriority)[keyof typeof SupplierPriority];

export const QuestionnaireStatus = {
   COMPLETED: 'completed',
   DRAFT: 'draft',
   REVIEW_PENDING: 'review_pending',
   SENT: 'sent',
} as const;
export type QuestionnaireStatusEnum = (typeof QuestionnaireStatus)[keyof typeof QuestionnaireStatus];

export const QuestionnaireResponseStatus = {
   DRAFT: 'DRAFT',
   REVIEW_PENDING: 'REVIEW_PENDING',
   COMPLETED: 'COMPLETED',
} as const;
export type QuestionnaireResponseStatusEnum = (typeof QuestionnaireResponseStatus)[keyof typeof QuestionnaireResponseStatus];

export const SupplierTier = {
   TIER_0: 'TIER_0',
   TIER_1_UPSTREAM: 'TIER_1_UPSTREAM',
   TIER_2_PLUS_UPSTREAM: 'TIER_2_PLUS_UPSTREAM',
   TIER_1_DOWNSTREAM: 'TIER_1_DOWNSTREAM',
   TIER_2_PLUS_DOWNSTREAM: 'TIER_2_PLUS_DOWNSTREAM',
} as const;
export type SupplierTierEnum = (typeof SupplierTier)[keyof typeof SupplierTier];

export const EmployeeSize = {
   SMALL: 'SMALL',
   MEDIUM: 'MEDIUM',
   LARGE: 'LARGE',
   ENTERPRISE: 'ENTERPRISE',
   MEGA_ENTERPRISE: 'MEGA_ENTERPRISE',
} as const;

export const TurnOver = {
   VERY_SMALL: 'VERY_SMALL',
   SMALL: 'SMALL',
   MEDIUM: 'MEDIUM',
   LARGE: 'LARGE',
   VERY_LARGE: 'VERY_LARGE',
   MEGA_GLOBAL: 'MEGA_GLOBAL',
} as const;
export type TurnOverEnum = (typeof TurnOver)[keyof typeof TurnOver];

export const InfluencingCapacity = {
   HIGH: 'HIGH',
   MEDIUM: 'MEDIUM',
   LOW: 'LOW',
} as const;
export type InfluencingCapacityEnum = (typeof InfluencingCapacity)[keyof typeof InfluencingCapacity];

export const SupplierSector = {
   A: 'AGRICULTURE_FORESTRY_AND_FISHING',
   B: 'MINING_AND_QUARRYING',
   C: 'MANUFACTURING',
   D: 'ELECTRICITY_GAS_STEAM_AND_AIR_CONDITIONING_SUPPLY',
   E: 'WATER_SUPPLY_SEWERAGE_WASTE_MANAGEMENT',
   F: 'CONSTRUCTION',
   G: 'WHOLESALE_AND_RETAIL_TRADE_REPAIR_OF_MOTOR_VEHICLES',
   H: 'TRANSPORTATION_AND_STORAGE',
   I: 'ACCOMMODATION_AND_FOOD_SERVICE_ACTIVITIES',
   J: 'INFORMATION_AND_COMMUNICATION',
   K: 'FINANCIAL_AND_INSURANCE_ACTIVITIES',
   L: 'REAL_ESTATE_ACTIVITIES',
   M: 'PROFESSIONAL_SCIENTIFIC_AND_TECHNICAL_ACTIVITIES',
   N: 'ADMINISTRATIVE_AND_SUPPORT_SERVICE_ACTIVITIES',
   O: 'PUBLIC_ADMINISTRATION_AND_DEFENCE_COMPULSORY_SOCIAL_SECURITY',
   P: 'EDUCATION',
   Q: 'HUMAN_HEALTH_AND_SOCIAL_WORK_ACTIVITIES',
   R: 'ARTS_ENTERTAINMENT_AND_RECREATION',
   S: 'OTHER_SERVICE_ACTIVITIES',
   T: 'ACTIVITIES_OF_HOUSEHOLDS_AS_EMPLOYERS_UNDIFFERENTIATED_GOODS',
   U: 'ACTIVITIES_OF_EXTRATERRITORIAL_ORGANISATIONS_AND_BODIES',
} as const;
export type SupplierSectorEnum = (typeof SupplierSector)[keyof typeof SupplierSector];

export const ActionType = {
   PREVENTIVE: 'PREVENTIVE',
   REMEDIAL: 'REMEDIAL',
} as const;
export type ActionTypeEnum = (typeof ActionType)[keyof typeof ActionType];
